/**
 * Smart Size Recommendation System
 * Automatically suggests recommended sizes and handles user confirmation
 */

class SizeRecommendationManager {
  constructor() {
    this.recommendationData = null;
    this.originalSelection = null;
    this.isProcessing = false;
    this.sizeOptionName = null;
    this.settings = {};
    this.currentRecommendedSize = null;
    this.recommendedSize = null;
    this.hasShownModalOnce = false;
    this.isProgrammaticChange = false;
    this.sizeFormatMap = new Map(); // Store original format of each size
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Load settings from the page
    this.loadSettings();
    
    // Check if feature is enabled
    if (!this.settings.enabled) {
      return;
    }
    
    // Get recommendation data from the page
    this.loadRecommendationData();
    
    // Build size format map
    this.buildSizeFormatMap();
    
    // Attach listeners to size inputs
    this.attachSizeListeners();
    
    // Create modal container
    this.createModal();
  }

  loadSettings() {
    // Get settings from data attribute
    const settingsElement = document.querySelector('[data-size-rec-settings]');
    if (settingsElement) {
      try {
        this.settings = JSON.parse(settingsElement.dataset.sizeRecSettings);
      } catch (e) {
        console.error('Failed to parse size recommendation settings:', e);
        this.settings = { enabled: false };
      }
    } else {
      this.settings = { enabled: false };
    }
  }

  loadRecommendationData() {
    // Get the recommendation value from your existing logic
    const recommendationElement = document.querySelector('[data-size-recommendation]');
    if (recommendationElement) {
      const recValue = recommendationElement.dataset.sizeRecommendation;
      this.recommendationData = this.parseRecommendation(recValue);
    }
  }

  parseRecommendation(value) {
    // Parse the recommendation value to get the offset
    const mapping = {
      '-1': { offset: -1, text: this.settings.hebrewOneSmallerText || 'one size smaller' },
      '-0.5': { offset: -0.5, text: this.settings.hebrewHalfSmallerText || 'half size smaller' },
      '0': { offset: 0, text: this.settings.hebrewTrueSizeText || 'true to size' },
      '+0.5': { offset: 0.5, text: this.settings.hebrewHalfUpText || 'half size up' },
      '0.5': { offset: 0.5, text: this.settings.hebrewHalfUpText || 'half size up' },
      '+1': { offset: 1, text: this.settings.hebrewOneUpText || 'one size up' },
      '1': { offset: 1, text: this.settings.hebrewOneUpText || 'one size up' },
      'One size smaller': { offset: -1, text: this.settings.hebrewOneSmallerText || 'one size smaller' },
      'Half size smaller': { offset: -0.5, text: this.settings.hebrewHalfSmallerText || 'half size smaller' },
      'True to size': { offset: 0, text: this.settings.hebrewTrueSizeText || 'true to size' },
      'Half size up': { offset: 0.5, text: this.settings.hebrewHalfUpText || 'half size up' },
      'One size up': { offset: 1, text: this.settings.hebrewOneUpText || 'one size up' }
    };

    return mapping[value] || null;
  }

  /**
   * Normalize size string to decimal format
   * Converts: "43 1/2" -> 43.5, "43,5" -> 43.5, "43.5" -> 43.5
   */
  normalizeSizeToDecimal(sizeStr) {
    if (!sizeStr) return null;
    
    const trimmed = sizeStr.trim();
    
    // Handle fraction format: "43 1/2" or "43½"
    const fractionMatch = trimmed.match(/^(\d+)\s*(\d+)\/(\d+)$/);
    if (fractionMatch) {
      const whole = parseInt(fractionMatch[1]);
      const numerator = parseInt(fractionMatch[2]);
      const denominator = parseInt(fractionMatch[3]);
      return whole + (numerator / denominator);
    }
    
    // Handle special fraction characters: "43½"
    if (trimmed.includes('½')) {
      const whole = parseFloat(trimmed.replace('½', ''));
      return whole + 0.5;
    }
    if (trimmed.includes('¼')) {
      const whole = parseFloat(trimmed.replace('¼', ''));
      return whole + 0.25;
    }
    if (trimmed.includes('¾')) {
      const whole = parseFloat(trimmed.replace('¾', ''));
      return whole + 0.75;
    }
    
    // Handle European decimal format: "43,5" -> "43.5"
    const europeanDecimal = trimmed.replace(',', '.');
    const parsed = parseFloat(europeanDecimal);
    
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Build a map of normalized sizes to their original formats
   */
  buildSizeFormatMap() {
    this.sizeFormatMap.clear();
    const allSizeInputs = this.findSizeInputs();
    
    allSizeInputs.forEach(input => {
      const originalSize = input.value;
      const normalizedSize = this.normalizeSizeToDecimal(originalSize);
      
      if (normalizedSize !== null) {
        // Store the original format for this normalized value
        if (!this.sizeFormatMap.has(normalizedSize)) {
          this.sizeFormatMap.set(normalizedSize, originalSize);
        }
      }
    });
  }

  /**
   * Format a decimal size back to the store's format
   */
  formatSizeToStoreFormat(decimalSize) {
    // First check if we have an exact match in our format map
    if (this.sizeFormatMap.has(decimalSize)) {
      return this.sizeFormatMap.get(decimalSize);
    }
    
    // If no exact match, try to infer the format from existing sizes
    const sampleFormats = Array.from(this.sizeFormatMap.values());
    
    if (sampleFormats.length > 0) {
      const firstSize = sampleFormats[0];
      
      // Check if store uses fractions (contains space and slash)
      if (firstSize.includes(' ') && firstSize.includes('/')) {
        return this.formatAsFraction(decimalSize);
      }
      
      // Check if store uses European format (comma)
      if (firstSize.includes(',')) {
        return this.formatAsEuropean(decimalSize);
      }
    }
    
    // Default to US decimal format
    return decimalSize % 1 === 0 ? decimalSize.toString() : decimalSize.toFixed(1);
  }

  formatAsFraction(decimalSize) {
    const whole = Math.floor(decimalSize);
    const decimal = decimalSize - whole;
    
    if (decimal === 0) {
      return whole.toString();
    } else if (decimal === 0.5) {
      return `${whole} 1/2`;
    } else if (decimal === 0.25) {
      return `${whole} 1/4`;
    } else if (decimal === 0.75) {
      return `${whole} 3/4`;
    } else {
      // Fallback to decimal
      return decimalSize.toFixed(1);
    }
  }

  formatAsEuropean(decimalSize) {
    const str = decimalSize % 1 === 0 ? decimalSize.toString() : decimalSize.toFixed(1);
    return str.replace('.', ',');
  }

  attachSizeListeners() {
    // Use event delegation on document to survive Shopify re-renders
    document.addEventListener('click', (e) => {
      // Check if clicked element is a size radio input
      const target = e.target;
      if (target.tagName === 'INPUT' && target.type === 'radio') {
        // Check if it's part of a size fieldset
        const fieldset = target.closest('fieldset.product-form__input');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const legendText = legend.textContent.trim().toLowerCase();
            if (legendText.includes('size') || legendText.includes('מידה')) {
              this.handleSizeClick(e);
            }
          }
        }
      }
    }, true); // Use capture phase to intercept before other handlers

    // Also handle select dropdowns
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target.tagName === 'SELECT') {
        const wrapper = target.closest('.product-form__input--dropdown');
        if (wrapper) {
          const label = target.previousElementSibling;
          if (label && (label.textContent.toLowerCase().includes('size') || label.textContent.includes('מידה'))) {
            this.handleSizeSelection(e);
          }
        }
      }
    }, true);
  }

  handleSizeClick(event) {
    // Ignore programmatic changes
    if (this.isProgrammaticChange) {
      return;
    }
    
    const selectedInput = event.target;
    const selectedSize = this.getSelectedSize(selectedInput);
    
    // If modal is open, don't process
    if (this.isProcessing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // Normalize sizes for comparison
    const normalizedSelected = this.normalizeSizeToDecimal(selectedSize);
    const normalizedCurrent = this.currentRecommendedSize ? this.normalizeSizeToDecimal(this.currentRecommendedSize) : null;
    
    // If user is clicking on the currently recommended size, just remove label and allow selection
    if (normalizedCurrent !== null && normalizedSelected === normalizedCurrent) {
      this.removeRecommendedLabels();
      this.currentRecommendedSize = null;
      return; // Allow the selection to proceed normally
    }
    
    // If user selects a different size, clear the current recommended state
    if (this.currentRecommendedSize) {
      this.removeRecommendedLabels();
      this.currentRecommendedSize = null;
      
      // Reset the modal shown flag when user manually changes size
      if (!this.settings.showModalOnce) {
        this.hasShownModalOnce = false;
      }
    }
    
    // Only process if we have recommendation data
    if (!this.recommendationData || this.recommendationData.offset === 0) return;

    // Check if modal should only show once
    if (this.settings.showModalOnce && this.hasShownModalOnce) {
      return; // Don't show modal again
    }

    // Calculate recommended size
    const recommendedSize = this.calculateRecommendedSize(selectedSize);
    
    // Store original selection
    this.originalSelection = {
      input: selectedInput,
      size: selectedSize
    };

    // Check if recommended size exists
    if (this.sizeExists(recommendedSize)) {
      // Prevent the default click from selecting
      event.preventDefault();
      event.stopPropagation();
      
      // Show confirmation modal
      this.showConfirmationModal(selectedSize, recommendedSize);
    }
  }

  findSizeInputs() {
    const inputs = [];
    
    // Look for size-related fieldsets or selects
    const sizeFieldsets = document.querySelectorAll('fieldset.product-form__input');
    
    sizeFieldsets.forEach(fieldset => {
      const legend = fieldset.querySelector('legend');
      if (legend) {
        const legendText = legend.textContent.trim().toLowerCase();
        // Check if it's a size option (support Hebrew "מידה" and English "size")
        if (legendText.includes('size') || legendText.includes('מידה')) {
          this.sizeOptionName = legend.textContent.split(':')[0].trim();
          const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
          inputs.push(...radioInputs);
        }
      }
    });

    // Also check for dropdown selects
    const selects = document.querySelectorAll('.product-form__input--dropdown select');
    selects.forEach(select => {
      const label = select.previousElementSibling;
      if (label && (label.textContent.toLowerCase().includes('size') || label.textContent.includes('מידה'))) {
        this.sizeOptionName = label.textContent.trim();
        inputs.push(select);
      }
    });

    return inputs;
  }

  handleSizeSelection(event) {
    // Ignore programmatic changes
    if (this.isProgrammaticChange) {
      return;
    }
    
    // If processing (modal is open), ignore all change events
    if (this.isProcessing) {
      return;
    }
    
    const selectedInput = event.target;
    const selectedSize = this.getSelectedSize(selectedInput);
    
    // If user manually selects any size and we have a recommended label visible, remove it
    if (this.currentRecommendedSize) {
      this.removeRecommendedLabels();
      this.currentRecommendedSize = null;
      return; // Allow the selection to proceed normally
    }
    
    // Only process if we have recommendation data
    if (!this.recommendationData || this.recommendationData.offset === 0) return;

    // Calculate recommended size
    const recommendedSize = this.calculateRecommendedSize(selectedSize);
    
    // Store original selection
    this.originalSelection = {
      input: selectedInput,
      size: selectedSize
    };

    // Check if recommended size exists
    if (this.sizeExists(recommendedSize)) {
      // Show confirmation modal
      this.showConfirmationModal(selectedSize, recommendedSize);
    }
    // If recommended size doesn't exist, do nothing (silent behavior)
  }

  getSelectedSize(input) {
    if (!input) return null;
    if (input.tagName === 'SELECT') {
      return input.value;
    } else {
      return input.value;
    }
  }

  calculateRecommendedSize(currentSize) {
    // Normalize the current size to decimal
    const sizeNum = this.normalizeSizeToDecimal(currentSize);
    if (sizeNum === null) return null;

    // Calculate recommended size in decimal
    const recommendedNum = sizeNum + this.recommendationData.offset;
    
    // Format back to store's format
    return this.formatSizeToStoreFormat(recommendedNum);
  }

  sizeExists(size) {
    if (!size) return false;
    
    const allSizeInputs = this.findSizeInputs();
    
    // Normalize the size we're looking for
    const normalizedTarget = this.normalizeSizeToDecimal(size);
    if (normalizedTarget === null) return false;
    
    return allSizeInputs.some(input => {
      const inputValue = input.value;
      const normalizedInput = this.normalizeSizeToDecimal(inputValue);
      
      if (input.tagName === 'SELECT') {
        const option = Array.from(input.querySelectorAll('option')).find(opt => {
          const normalizedOpt = this.normalizeSizeToDecimal(opt.value);
          return normalizedOpt === normalizedTarget && !opt.disabled;
        });
        return !!option;
      } else {
        // For radio buttons, check if normalized values match
        return normalizedInput === normalizedTarget && 
               !input.classList.contains('disabled') && 
               !input.disabled;
      }
    });
  }

  createModal() {
    // Create modal HTML
    const modalHTML = `
      <div id="size-recommendation-modal" class="size-rec-modal" style="display: none;">
        <div class="size-rec-modal-overlay"></div>
        <div class="size-rec-modal-content">
          <div class="size-rec-modal-body">
            <p class="size-rec-selected">${this.settings.modalSelectedText || 'You selected size'} <strong id="selected-size"></strong></p>
            <p class="size-rec-title">${this.settings.modalTitleText || 'For this model we recommend taking'} <span id="rec-text"></span>*</p>
            <p class="size-rec-message">${this.settings.modalMessageText || 'We updated the size to the recommended'} <strong id="rec-size"></strong></p>
            <p class="size-rec-confirm">${this.settings.modalConfirmText || 'Please confirm'}</p>
          </div>
          <div class="size-rec-modal-actions">
            <button id="size-rec-yes" class="size-rec-btn size-rec-btn-yes">${this.settings.modalYesText || 'YES'}</button>
            <button id="size-rec-no" class="size-rec-btn size-rec-btn-no">${this.settings.modalNoText || 'NO'}</button>
          </div>
        </div>
      </div>
    `;

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Add styles
    this.addModalStyles();

    // Attach button listeners
    document.getElementById('size-rec-yes').addEventListener('click', () => this.confirmRecommendation());
    document.getElementById('size-rec-no').addEventListener('click', () => this.rejectRecommendation());
    
    // Close modal on overlay click
    document.querySelector('.size-rec-modal-overlay').addEventListener('click', () => this.rejectRecommendation());
  }

  addModalStyles() {
    const overlayStyles = this.settings.showOverlay !== false 
      ? `background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(2px);` 
      : `background: transparent; pointer-events: none;`;
    
    const styles = `
      <style>
        .size-rec-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .size-rec-modal-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          ${overlayStyles}
        }
        
        .size-rec-modal-content {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: ${this.settings.modalBgColor || '#ffffff'};
          border-radius: ${this.settings.modalBorderRadius || '12px'};
          padding: ${this.settings.modalPadding || '35px'};
          max-width: 500px;
          width: 90%;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.3s ease;
          border: 2px solid ${this.settings.modalBorderColor || '#abd4f2'};
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -45%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
        
        .size-rec-modal-body {
          text-align: center;
          margin-bottom: 25px;
        }
        
        .size-rec-selected {
          font-size: ${this.settings.selectedFontSize || '15px'};
          margin-bottom: 18px;
          color: ${this.settings.selectedColor || '#555555'};
          line-height: 1.6;
          font-weight: 400;
        }
        
        .size-rec-selected strong {
          color: ${this.settings.selectedHighlight || '#000000'};
          font-weight: 700;
          font-size: ${this.settings.selectedHighlightSize || '16px'};
        }
        
        .size-rec-title {
          font-size: ${this.settings.titleFontSize || '17px'};
          margin-bottom: 20px;
          color: ${this.settings.titleColor || '#000000'};
          line-height: 1.6;
          font-weight: 600;
        }
        
        .size-rec-title span {
          color: ${this.settings.recommendationTypeColor || '#000000'};
          font-weight: 700;
        }
        
        .size-rec-message {
          font-size: ${this.settings.messageFontSize || '19px'};
          margin-bottom: 20px;
          color: ${this.settings.messageColor || '#000000'};
          font-weight: 600;
          line-height: 1.5;
        }
        
        .size-rec-message strong {
          color: ${this.settings.highlightColor || '#d4af37'};
          font-weight: 700;
          font-size: ${this.settings.highlightFontSize || '28px'};
          display: inline-block;
          margin: 0 4px;
        }
        
        .size-rec-confirm {
          font-size: ${this.settings.confirmFontSize || '15px'};
          color: ${this.settings.confirmColor || '#666666'};
          margin-bottom: 0;
          line-height: 1.5;
        }
        
        .size-rec-modal-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 25px;
        }
        
        .size-rec-btn {
          padding: ${this.settings.buttonPadding || '14px 45px'};
          border: none;
          border-radius: ${this.settings.buttonBorderRadius || '6px'};
          font-size: ${this.settings.buttonFontSize || '16px'};
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .size-rec-btn-yes {
          background: ${this.settings.yesButtonBg || '#000000'};
          color: ${this.settings.yesButtonColor || '#ffffff'};
        }
        
        .size-rec-btn-yes:hover {
          background: ${this.settings.yesButtonHoverBg || '#333333'};
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .size-rec-btn-no {
          background: ${this.settings.noButtonBg || '#e0e0e0'};
          color: ${this.settings.noButtonColor || '#333333'};
        }
        
        .size-rec-btn-no:hover {
          background: ${this.settings.noButtonHoverBg || '#c0c0c0'};
          transform: translateY(-2px);
        }
        
        /* Recommended label for size buttons */
        .size-rec-label {
          position: absolute;
          top: -8px;
          right: -8px;
          background: ${this.settings.labelBgColor || '#d4af37'};
          color: ${this.settings.labelTextColor || '#ffffff'};
          font-size: ${this.settings.labelFontSize || '10px'};
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          z-index: 10;
          pointer-events: none;
        }
        
        /* Style for parent label to accommodate the badge */
        .size-rec-parent {
          position: relative;
        }
        
        @media (max-width: 480px) {
          .size-rec-modal-content {
            padding: 25px 20px;
            max-width: 95%;
          }
          
          .size-rec-btn {
            padding: 12px 30px;
            font-size: 14px;
          }
          
          .size-rec-title {
            font-size: 16px;
          }
          
          .size-rec-message {
            font-size: 17px;
          }
          
          .size-rec-message strong {
            font-size: 24px;
          }
        }
      </style>
    `;
    
    document.head.insertAdjacentHTML('beforeend', styles);
  }

  showConfirmationModal(selectedSize, recommendedSize) {
    this.isProcessing = true;
    this.recommendedSize = recommendedSize;
    this.hasShownModalOnce = true; // Mark that modal has been shown
    
    const modal = document.getElementById('size-recommendation-modal');
    const selectedSizeSpan = document.getElementById('selected-size');
    const recText = document.getElementById('rec-text');
    const recSize = document.getElementById('rec-size');
    
    // Update modal content
    selectedSizeSpan.textContent = selectedSize;
    recText.textContent = this.recommendationData.text;
    recSize.textContent = recommendedSize;
    
    // Switch to recommended size first (without events)
    this.switchToSize(recommendedSize);
    
    // Add recommended label immediately
    this.addRecommendedLabel(recommendedSize);
    
    // Show modal after everything is set
    modal.style.display = 'block';
  }

  switchToSize(size, triggerEvents = false) {
    const allSizeInputs = this.findSizeInputs();
    
    // Set flag before triggering events
    if (triggerEvents) {
      this.isProgrammaticChange = true;
    }
    
    // Normalize the target size for comparison
    const normalizedTarget = this.normalizeSizeToDecimal(size);
    
    allSizeInputs.forEach(input => {
      if (input.tagName === 'SELECT') {
        // For select, find the option that matches
        const matchingOption = Array.from(input.querySelectorAll('option')).find(opt => {
          const normalizedOpt = this.normalizeSizeToDecimal(opt.value);
          return normalizedOpt === normalizedTarget;
        });
        
        if (matchingOption) {
          input.value = matchingOption.value;
          if (triggerEvents) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } else {
        // For radio buttons, check normalized value
        const normalizedInput = this.normalizeSizeToDecimal(input.value);
        if (normalizedInput === normalizedTarget) {
          input.checked = true;
          if (triggerEvents) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });
    
    // Reset flag after a short delay to ensure all events are processed
    if (triggerEvents) {
      setTimeout(() => {
        this.isProgrammaticChange = false;
      }, 100);
    }
  }

  addRecommendedLabel(size) {
    // Remove any existing labels first
    this.removeRecommendedLabels();
    
    const allSizeInputs = this.findSizeInputs();
    const labelText = this.settings.recommendedLabelText || 'Recommended';
    
    // Normalize the target size for comparison
    const normalizedTarget = this.normalizeSizeToDecimal(size);
    
    allSizeInputs.forEach(input => {
      const normalizedInput = this.normalizeSizeToDecimal(input.value);
      
      if (normalizedInput === normalizedTarget) {
        if (input.tagName !== 'SELECT') {
          // For radio buttons, add label to the associated label element
          const label = document.querySelector(`label[for="${input.id}"]`);
          if (label && !label.querySelector('.size-rec-label')) {
            // Make label position relative
            label.classList.add('size-rec-parent');
            
            // Create and add the badge
            const badge = document.createElement('span');
            badge.className = 'size-rec-label';
            badge.textContent = labelText;
            label.appendChild(badge);
          }
        }
      }
    });
  }

  removeRecommendedLabels() {
    const existingLabels = document.querySelectorAll('.size-rec-label');
    existingLabels.forEach(label => label.remove());
    
    const parentLabels = document.querySelectorAll('.size-rec-parent');
    parentLabels.forEach(label => label.classList.remove('size-rec-parent'));
  }

  confirmRecommendation() {
    // Store the confirmed recommended size
    this.currentRecommendedSize = this.recommendedSize;
    
    // Close modal
    this.closeModal();
    
    // Reset processing state
    this.isProcessing = false;
    this.originalSelection = null;
    
    // Trigger change events so Shopify variant system updates
    // Use a small delay to ensure modal is closed first
    setTimeout(() => {
      this.switchToSize(this.recommendedSize, true);
    }, 50);
    
    // Reset the flag if not in "once" mode
    if (!this.settings.showModalOnce) {
      this.hasShownModalOnce = false;
    }
  }

  rejectRecommendation() {
    // Only show label if "show on once" mode OR if "show label on reject" is enabled
    const shouldShowLabel = this.settings.showModalOnce || this.settings.showLabelOnReject;
    
    if (shouldShowLabel) {
      // Store the recommended size so label stays
      this.currentRecommendedSize = this.recommendedSize;
    } else {
      // Remove the label
      this.removeRecommendedLabels();
      this.currentRecommendedSize = null;
    }
    
    // Close modal
    this.closeModal();
    
    // Reset processing state
    this.isProcessing = false;
    
    // Store original selection before clearing
    const originalSizeToRestore = this.originalSelection ? this.originalSelection.size : null;
    this.originalSelection = null;
    
    // Revert to original selection (with events to update variant)
    setTimeout(() => {
      if (originalSizeToRestore) {
        this.switchToSize(originalSizeToRestore, true);
      }
    }, 50);
    
    // Reset the flag if not in "once" mode
    if (!this.settings.showModalOnce) {
      this.hasShownModalOnce = false;
    }
    
    // Label stays visible on recommended size if shouldShowLabel is true
  }

  closeModal() {
    const modal = document.getElementById('size-recommendation-modal');
    modal.style.display = 'none';
  }
}

// Initialize the size recommendation system
const sizeRecManager = new SizeRecommendationManager();